import { useState, useEffect, useMemo } from 'react';
import { Loader2, Search, FileText, Building2, ArrowLeftRight, AlertTriangle, Check, Layers, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { matchingAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { directionMismatch } from '../lib/reconciliationDirection';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import { format } from 'date-fns';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy'); } catch (e) { return String(d); }
}

// Same amount tolerance the backend enforces for a group's itemized amounts
// (routes/matching.py::confirm_group_match / create_manual_group_match) --
// duplicated here ONLY for enable/disable UX; the server re-checks it, this
// never substitutes for that check.
var GROUP_SUM_TOLERANCE = 0.01;

/**
 * Operator-driven match picker. Candidates come from
 * matchingAPI.getCandidateInvoices, which applies the SAME eligibility rules
 * as the auto-matcher's own candidate pool (date scope, settled-invoice
 * exclusion, review gate -- see services/matching_candidates.py) scoped to
 * the transaction's own entity, so a rule the auto-matcher enforces cannot
 * be routed around by picking manually instead. Selecting a candidate shows
 * a side-by-side Preview with a direction-mismatch warning; Apply routes
 * through matchingAPI.manualMatch, which creates then confirms via the
 * shared confirm path (inheriting the double-match 409 guard, accounting
 * sync, and intercompany creation).
 *
 * Group match mode (Layers toggle): select several invoices for this one
 * transaction -- the standard "one payment covers several invoices" B2B
 * settlement pattern. Candidates are pre-filtered to the transaction's own
 * counterparty (an explicit checkbox opts into cross-counterparty), a
 * running sum gates Confirm to within the server's own tolerance, and a
 * bounded exact-sum suggestion (matchingAPI.groupSuggestion) can be applied
 * with one click when unambiguous. Confirm routes through matchingAPI.
 * manualGroupMatch, creating then confirming via confirm_group_match --
 * never auto-confirmed, always this explicit human action.
 */
export function ManualMatchDialog({ transaction, open, onClose, onApplied }) {
  var [invoices, setInvoices] = useState([]);
  var [loading, setLoading] = useState(false);
  var [query, setQuery] = useState('');
  var [selected, setSelected] = useState(null);
  var [applying, setApplying] = useState(false);

  // Group match mode: select several invoices for one transaction (see
  // matchingAPI.manualGroupMatch). Off by default -- the ordinary 1:1 flow
  // is unchanged; this is an additive toggle.
  var [groupMode, setGroupMode] = useState(false);
  var [selectedIds, setSelectedIds] = useState(function () { return new Set(); });
  var [includeOtherCounterparties, setIncludeOtherCounterparties] = useState(false);
  var [suggestion, setSuggestion] = useState(null);

  useEffect(function () {
    if (!open || !transaction) return;
    var cancelled = false;
    setLoading(true);
    setSelected(null);
    setQuery('');
    setGroupMode(false);
    setSelectedIds(new Set());
    setIncludeOtherCounterparties(false);
    setSuggestion(null);
    // Server enforces eligibility (date scope, settled, review gate) --
    // see matchingAPI.getCandidateInvoices.
    matchingAPI.getCandidateInvoices(transaction.id)
      .then(function (res) {
        if (cancelled) return;
        setInvoices((res.data && res.data.candidates) || []);
      })
      .catch(function (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      })
      .finally(function () { if (!cancelled) setLoading(false); });
    // A group suggestion is fetched unconditionally (cheap, bounded, single-
    // transaction) so the "Suggested" badge can render as soon as the
    // operator switches into group mode, without an extra round trip.
    matchingAPI.groupSuggestion(transaction.id)
      .then(function (res) {
        if (cancelled) return;
        setSuggestion((res.data && res.data.suggestion) || null);
      })
      .catch(function () { /* suggestion is a convenience, never blocks manual selection */ });
    return function () { cancelled = true; };
  }, [open, transaction]);

  var filtered = useMemo(function () {
    var q = query.trim().toLowerCase();
    var base = invoices;
    if (groupMode && !includeOtherCounterparties && transaction && transaction.counterparty) {
      var cp = transaction.counterparty.trim().toLowerCase();
      base = base.filter(function (i) { return (i.supplier_client_name || '').trim().toLowerCase() === cp; });
    }
    if (!q) return base;
    return base.filter(function (i) {
      var name = (i.supplier_client_name || '').toLowerCase();
      var num = (i.invoice_number || '').toLowerCase();
      var amt = i.amount != null ? String(i.amount) : '';
      return name.indexOf(q) !== -1 || num.indexOf(q) !== -1 || amt.indexOf(q) !== -1;
    });
  }, [invoices, query, groupMode, includeOtherCounterparties, transaction]);

  var selectedGroupInvoices = useMemo(function () {
    return invoices.filter(function (i) { return selectedIds.has(i.id); });
  }, [invoices, selectedIds]);

  var groupTotal = useMemo(function () {
    return selectedGroupInvoices.reduce(function (sum, i) { return sum + (Number(i.amount) || 0); }, 0);
  }, [selectedGroupInvoices]);

  var groupCurrenciesMixed = useMemo(function () {
    var currencies = new Set(selectedGroupInvoices.map(function (i) { return i.currency; }).filter(Boolean));
    return currencies.size > 1 || (transaction && currencies.size === 1 && !currencies.has(transaction.currency));
  }, [selectedGroupInvoices, transaction]);

  var txAmountAbs = transaction ? Math.abs(Number(transaction.amount) || 0) : 0;
  var groupSumMatches = !groupCurrenciesMixed && Math.abs(groupTotal - txAmountAbs) <= GROUP_SUM_TOLERANCE;
  var groupReadyToConfirm = selectedIds.size >= 2 && groupSumMatches;

  var toggleGroupSelection = function (inv) {
    setSelectedIds(function (prev) {
      var next = new Set(prev);
      if (next.has(inv.id)) next.delete(inv.id); else next.add(inv.id);
      return next;
    });
  };

  var applySuggestion = function () {
    if (!suggestion) return;
    setSelectedIds(new Set(suggestion.invoice_ids));
    setIncludeOtherCounterparties(true); // the suggestion may include ids filtered out by the counterparty prefilter's exact-match
  };

  var mismatch = selected && transaction
    ? directionMismatch(transaction.amount, selected.type)
    : false;

  var handleApply = function () {
    if (!selected || !transaction) return;
    setApplying(true);
    matchingAPI.manualMatch(transaction.id, selected.id)
      .then(function () {
        toast.success('Transaction matched');
        setApplying(false);
        onApplied && onApplied();
      })
      .catch(function (err) {
        setApplying(false);
        toast.error(getErrorMessage(err));
      });
  };

  var handleApplyGroup = function () {
    if (!groupReadyToConfirm || !transaction) return;
    setApplying(true);
    matchingAPI.manualGroupMatch(transaction.id, Array.from(selectedIds))
      .then(function () {
        toast.success('Transaction matched to ' + selectedIds.size + ' invoices');
        setApplying(false);
        onApplied && onApplied();
      })
      .catch(function (err) {
        setApplying(false);
        toast.error(getErrorMessage(err));
      });
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={function (o) { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="manual-match-dialog">
        <DialogHeader>
          <DialogTitle>Match manually</DialogTitle>
          <DialogDescription>
            Pick an invoice to reconcile against this transaction, or switch to Group match
            to settle several invoices with one payment. Applying runs the same confirmation
            as an auto-suggested match.
          </DialogDescription>
        </DialogHeader>

        {/* Transaction summary */}
        <div className="rounded-lg border p-3 bg-muted/40 text-sm flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="flex items-center gap-1.5 font-medium">
            <Building2 className="h-4 w-4 text-primary" /> Transaction
          </span>
          <span className="text-muted-foreground">{fmtDate(transaction.date)}</span>
          <span className="font-mono font-medium">{formatCurrency(transaction.amount, transaction.currency)}</span>
          <span className="truncate max-w-[220px]">{transaction.counterparty || transaction.description || '—'}</span>
        </div>

        {!selected ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex rounded-lg border p-0.5" role="group" aria-label="Match mode">
                <Button
                  type="button" size="sm" variant={groupMode ? 'ghost' : 'secondary'}
                  className="rounded-md" onClick={function () { setGroupMode(false); setSelectedIds(new Set()); }}
                  data-testid="manual-match-mode-single"
                >
                  Single invoice
                </Button>
                <Button
                  type="button" size="sm" variant={groupMode ? 'secondary' : 'ghost'}
                  className="rounded-md" onClick={function () { setGroupMode(true); }}
                  data-testid="manual-match-mode-group"
                >
                  <Layers className="h-3.5 w-3.5 mr-1" /> Group match
                </Button>
              </div>
              {groupMode && suggestion && suggestion.invoice_ids && suggestion.invoice_ids.length > 0 && (
                <Button type="button" size="sm" variant="outline" onClick={applySuggestion} data-testid="manual-match-apply-suggestion">
                  <Sparkles className="h-3.5 w-3.5 mr-1 text-primary" />
                  Suggested group ({suggestion.invoice_ids.length} invoices)
                </Button>
              )}
            </div>

            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search by counterparty, invoice number, or amount"
                value={query}
                onChange={function (e) { setQuery(e.target.value); }}
                data-testid="manual-match-search"
              />
            </div>

            {groupMode && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={includeOtherCounterparties}
                  onCheckedChange={function (v) { setIncludeOtherCounterparties(!!v); }}
                  data-testid="manual-match-include-other-counterparties"
                />
                Include invoices from other counterparties
              </label>
            )}

            <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {groupMode
                    ? 'No candidates for this counterparty. Try "Include invoices from other counterparties".'
                    : 'No unmatched invoices for this entity.'}
                </p>
              ) : groupMode ? (
                filtered.map(function (inv) {
                  var checked = selectedIds.has(inv.id);
                  return (
                    <label
                      key={inv.id}
                      className="w-full text-left p-3 hover:bg-muted/50 flex items-center justify-between gap-3 cursor-pointer"
                      data-testid={'manual-group-match-candidate-' + inv.id}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Checkbox checked={checked} onCheckedChange={function () { toggleGroupSelection(inv); }} />
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <span className="truncate">
                          <span className="font-mono text-xs">{inv.invoice_number || '—'}</span>
                          <span className="text-muted-foreground"> · {inv.supplier_client_name || '—'}</span>
                        </span>
                      </span>
                      <span className="font-mono text-sm shrink-0">{formatCurrency(inv.amount, inv.currency)}</span>
                    </label>
                  );
                })
              ) : (
                filtered.map(function (inv) {
                  return (
                    <button
                      key={inv.id}
                      onClick={function () { setSelected(inv); }}
                      className="w-full text-left p-3 hover:bg-muted/50 flex items-center justify-between gap-3"
                      data-testid={'manual-match-candidate-' + inv.id}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <span className="truncate">
                          <span className="font-mono text-xs">{inv.invoice_number || '—'}</span>
                          <span className="text-muted-foreground"> · {inv.supplier_client_name || '—'}</span>
                        </span>
                      </span>
                      <span className="font-mono text-sm shrink-0">{formatCurrency(inv.amount, inv.currency)}</span>
                    </button>
                  );
                })
              )}
            </div>

            {groupMode && (
              <div
                className={
                  'rounded-lg border p-3 text-sm flex items-center justify-between gap-3 ' +
                  (groupReadyToConfirm ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'bg-muted/40')
                }
                data-testid="manual-group-match-running-sum"
              >
                <span className="text-muted-foreground">
                  {selectedIds.size} invoice{selectedIds.size === 1 ? '' : 's'} selected
                  {groupCurrenciesMixed && selectedIds.size > 0 && (
                    <span className="text-amber-600 dark:text-amber-400"> · currency mismatch</span>
                  )}
                </span>
                <span className="font-mono font-medium">
                  {formatCurrency(groupTotal, transaction.currency)} / {formatCurrency(txAmountAbs, transaction.currency)}
                </span>
              </div>
            )}
          </div>
        ) : (
          /* Preview: transaction vs invoice side by side */
          <div className="space-y-3" data-testid="manual-match-preview">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
              <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1.5">
                <div className="flex items-center gap-1.5 font-medium mb-1"><Building2 className="h-4 w-4 text-primary" /> Transaction</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{fmtDate(transaction.date)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono font-medium">{formatCurrency(transaction.amount, transaction.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Counterparty</span><span className="truncate max-w-[140px]">{transaction.counterparty || '—'}</span></div>
              </div>
              <div className="flex items-center justify-center"><ArrowLeftRight className="h-5 w-5 text-primary" /></div>
              <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1.5">
                <div className="flex items-center gap-1.5 font-medium mb-1"><FileText className="h-4 w-4 text-primary" /> Invoice</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Number</span><span className="font-mono">{selected.invoice_number || '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono font-medium">{formatCurrency(selected.amount, selected.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{selected.type === 'inbound' ? 'Supplier' : 'Client'}</span><span className="truncate max-w-[140px]">{selected.supplier_client_name || '—'}</span></div>
              </div>
            </div>

            {transaction.currency && selected.currency && transaction.currency !== selected.currency && (
              <p className="text-xs text-muted-foreground">
                Cross-currency pairing: transaction in {transaction.currency}, invoice in {selected.currency}.
                Amounts are shown in their native currencies (not converted).
              </p>
            )}

            {mismatch && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm" data-testid="manual-match-direction-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <span className="text-amber-800 dark:text-amber-300">
                  Direction mismatch: a {transaction.amount < 0 ? 'debit (money out)' : 'credit (money in)'} is being paired with
                  a {selected.type === 'inbound' ? 'payable' : 'receivable'} invoice. Confirm this is intended (for example a credit note).
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {selected && (
            <Button variant="outline" onClick={function () { setSelected(null); }} disabled={applying}>
              Back
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={applying}>Cancel</Button>
          {groupMode ? (
            <Button onClick={handleApplyGroup} disabled={!groupReadyToConfirm || applying} data-testid="manual-group-match-apply">
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Confirm group ({selectedIds.size})
            </Button>
          ) : (
            <Button onClick={handleApply} disabled={!selected || applying} data-testid="manual-match-apply">
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
